import { ActivityDirection, LevelBreakdown, PeriodTotals, ShareTotals } from "./model";

/**
 * The share line under each card title, ported from gen-ai-ui's
 * `riskShareMetrics` (Sep 2026):
 *
 *   "0.43% of employee shares to AI · ↓ 37% vs last month"
 *
 * The first figure is the card's headline as a share of a pool. The second is
 * how the share itself moved against the previous period — a relative change,
 * so 0.40% -> 0.30% reads "↓ 25%", not "↓ 0.1".
 *
 * The prototype's pools are demo constants and its previous share a scaled
 * baseline. Here both periods are real counts (see api.ts §7-8), and the
 * trend compares like with like: the same numerator over the same pool, this
 * period against the last.
 *
 * What each line divides (frontend fix list F2, Oct 2026) — same period, same
 * six category families (data/categories.ts), same counting unit on both
 * sides:
 *
 *   Risks cards        the card's risky activities, at the slider's severities
 *                      / that direction's AI activities, at any severity
 *   Visibility cards   the card's AI activities, at the slider's sensitivities
 *                      / every activity counted in those categories, AI or
 *                        not, at any sensitivity
 *
 * Both sides are sums of per-category counts, so neither is a count of unique
 * items, and the labels do not say so. A pool of zero has no share: the line
 * is left out rather than printed as "0.00%".
 */

export const format_share_of_total = (count: number, total: number) =>
  total > 0 ? `${((count / total) * 100).toFixed(2)}%` : "";

/** "↑ 3%", "↓ 37%", "→ 0%", or "—" when there is nothing to compare with */
export const format_share_change = (current: number, previous: number) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return "—";
  }
  const rel = ((current - previous) / previous) * 100;
  const abs = Math.abs(rel).toFixed(0);
  if (rel < 0) return `↓ ${abs}%`;
  if (rel > 0) return `↑ ${abs}%`;
  return "→ 0%";
};

const ratio = (part: number, whole: number) => (whole > 0 ? part / whole : NaN);

const line = (
  shares: ShareTotals,
  count: number,
  pool: (p: PeriodTotals) => number,
  part: (p: PeriodTotals) => number,
  pool_label: string
) => {
  const share = format_share_of_total(count, pool(shares.current));
  // nothing to divide by — the headline stands on its own
  if (!share) return "";
  return (
    `${share} ${pool_label} · ` +
    `${format_share_change(
      ratio(part(shares.current), pool(shares.current)),
      ratio(part(shares.previous), pool(shares.previous))
    )} vs last ${shares.period}`
  );
};

const risky_at = (
  p: PeriodTotals,
  direction: ActivityDirection,
  levels: readonly (keyof LevelBreakdown)[]
) => levels.reduce((sum, k) => sum + p.risky[direction][k], 0);

/**
 * The two Risks cards: risky AI events as a share of that direction's AI
 * events. Both sides come from the events list, which is the only source
 * that knows an AI activity's severity.
 */
export const risk_share_line = (
  shares: ShareTotals,
  direction: ActivityDirection,
  headline: number,
  levels: readonly (keyof LevelBreakdown)[]
) =>
  line(
    shares,
    headline,
    (p) => p.ai_events[direction],
    (p) => risky_at(p, direction, levels),
    direction === "employee"
      ? "of employee shares to AI"
      : "of AI-originated activities"
  );

/** what the Visibility cards' pool is, as their lines name it */
export const VISIBILITY_POOL_LABEL = "of all activity across these categories";

/**
 * The two Visibility cards: AI activity as a share of every activity counted
 * in the same six categories. One pool for both cards, so one label.
 */
export const visibility_share_line = (
  shares: ShareTotals,
  direction: ActivityDirection,
  headline: number
) =>
  line(
    shares,
    headline,
    (p) => p.events,
    (p) => p.activity[direction],
    VISIBILITY_POOL_LABEL
  );
